import React from 'react';
import intertidalIconAsset from '../../../resources/icone_intertidal_png.png';
import mepbayIconAsset from '../../../resources/icone_mepbay_sem_circulo.png';
import euscoastIconAsset from '../../../resources/icone_euscoast.png';

/**
 * Line-art icon set for the module selection cards. Each icon shares the same
 * viewBox/stroke language (thin rounded strokes, no fill) so they read as one
 * consistent family, while the pictogram itself reflects what that module does.
 * `color` lets each icon pick up its module's theme color.
 */
const iconProps = (color, strokeWidth = 3) => ({
  viewBox: '0 0 100 70',
  width: '100%',
  height: '100%',
  fill: 'none',
  stroke: color,
  strokeWidth,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

// Bathymetry: satellite + sun reflecting light down onto a submerged coastal
// profile (optical depth inversion).
export const BathymetryIcon = ({ color = '#0B1F63' }) => (
  <svg {...iconProps(color)}>
    <g transform="rotate(-25 18 16)">
      <rect x="12" y="10" width="12" height="9" rx="1.5" />
      <rect x="0" y="6" width="9" height="6" rx="1" />
      <rect x="27" y="16" width="9" height="6" rx="1" />
      <line x1="9" y1="9.5" x2="12" y2="11.5" />
      <line x1="25" y1="17" x2="29" y2="19.5" />
      <circle cx="18" cy="10" r="1.6" fill={color} stroke="none" />
      <line x1="18" y1="10" x2="18" y2="4" />
    </g>
    <circle cx="65" cy="14" r="7" />
    <line x1="65" y1="1" x2="65" y2="5" />
    <line x1="65" y1="23" x2="65" y2="27" />
    <line x1="52" y1="14" x2="56" y2="14" />
    <line x1="77" y1="14" x2="81" y2="14" />
    <line x1="52.5" y1="5.5" x2="55" y2="8" />
    <line x1="52.5" y1="22.5" x2="55" y2="20" />
    <line x1="77.5" y1="5.5" x2="75" y2="8" />
    <line x1="77.5" y1="22.5" x2="75" y2="20" />
    <line x1="20" y1="20" x2="32" y2="65" />
    <line x1="65" y1="16" x2="32" y2="65" />
    <path d="M27 59 L 32 65 L 38 60" />
    <path d="M2 46 Q 10 42, 18 46 T 34 44 Q 42 42, 48 46 T 62 42 Q 74 34, 82 24 T 98 6" />
    <path d="M4 67 Q 20 63, 40 66 T 70 65 T 98 61" opacity="0.5" />
  </svg>
);

// Shoreline analysis: a coastline crossed by measurement transects, one
// showing the change being tracked over time.
export const ShorelineIcon = ({ color = '#0B1F63' }) => (
  <svg {...iconProps(color)}>
    <path d="M4 40 Q 20 30, 36 38 T 68 38 T 96 32" />
    <line x1="20" y1="20" x2="20" y2="52" />
    <line x1="50" y1="20" x2="50" y2="52" />
    <line x1="80" y1="20" x2="80" y2="52" />
    <path d="M50 14 L 46 20 L 54 20 Z" fill={color} stroke="none" />
    <path d="M50 58 L 46 52 L 54 52 Z" fill={color} stroke="none" />
  </svg>
);

// Coastal squeeze: mangrove vegetation pinched between a rising waterline and
// a fixed barrier.
export const CSqueezeIcon = ({ color = '#0B1F63' }) => (
  <svg {...iconProps(color)}>
    <line x1="82" y1="8" x2="82" y2="62" />
    <line x1="88" y1="8" x2="88" y2="62" />
    <line x1="82" y1="14" x2="88" y2="8" />
    <line x1="82" y1="26" x2="88" y2="20" />
    <line x1="82" y1="38" x2="88" y2="32" />
    <line x1="82" y1="50" x2="88" y2="44" />
    <circle cx="45" cy="26" r="14" />
    <line x1="45" y1="40" x2="45" y2="58" />
    <path d="M4 50 Q 14 44, 24 50 T 44 50" />
    <path d="M4 58 Q 14 52, 24 58 T 44 58" opacity="0.5" />
    <line x1="58" y1="54" x2="70" y2="54" />
    <path d="M65 50 L 70 54 L 65 58" />
  </svg>
);

// EUSCoast: custom artwork provided by the team (urban skyline with the projected
// expansion dashed out), already colored to match the module's red theme.
export const EUSCoastIcon = () => (
  <img src={euscoastIconAsset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
);

// Intertidal: custom artwork provided by the team (satellite/sun/tide-gauge
// motif), already colored to match the module's amber theme.
export const IntertidalIcon = () => (
  <img src={intertidalIconAsset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
);

// MEPBay: custom artwork provided by the team (equilibrium bay planform
// between two headlands), already colored to match the module's blue theme.
export const MepBayIcon = () => (
  <img src={mepbayIconAsset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
);

// Shoreline-R: historical time-series samples extrapolated into a projected
// (dashed) future trend.
export const ShoreRIcon = ({ color = '#0B1F63' }) => (
  <svg {...iconProps(color)}>
    <line x1="6" y1="60" x2="6" y2="8" />
    <line x1="6" y1="60" x2="94" y2="60" />
    <circle cx="18" cy="46" r="2.5" fill={color} stroke="none" />
    <circle cx="32" cy="40" r="2.5" fill={color} stroke="none" />
    <circle cx="46" cy="34" r="2.5" fill={color} stroke="none" />
    <path d="M18 46 L 32 40 L 46 34" />
    <path d="M46 34 L 88 16" strokeDasharray="5 5" />
    <path d="M81 12 L 88 16 L 83 22" />
  </svg>
);

export const ShoreSIcon = ({ color = '#0B1F63' }) => (
  <svg {...iconProps(color)}>
    <path d="M4 46 Q 25 40, 46 44 T 90 41" />
    <path d="M4 44 Q 25 34, 46 32 T 90 14" strokeDasharray="5 5" />
    <line x1="46" y1="44" x2="46" y2="32" strokeWidth="2" opacity="0.6" />
    <line x1="90" y1="41" x2="90" y2="14" strokeWidth="2" opacity="0.6" />
    <circle cx="46" cy="44" r="2.2" fill={color} stroke="none" />
    <circle cx="46" cy="32" r="2.2" fill={color} stroke="none" />
    <circle cx="90" cy="41" r="2.2" fill={color} stroke="none" />
    <circle cx="90" cy="14" r="2.2" fill={color} stroke="none" />
  </svg>
);

export default {
  shoreline: ShorelineIcon,
  bathymetry: BathymetryIcon,
  csqueeze: CSqueezeIcon,
  euscoast: EUSCoastIcon,
  intermare: IntertidalIcon,
  mepbay: MepBayIcon,
  shore_r: ShoreRIcon,
  shore_s: ShoreSIcon,
};
